import { Fragment, useMemo, useState } from "react";
import { Btn } from "../../../AbstractElements";
import {
  FormGroup,
  Label,
  Modal,
  ModalHeader,
  ModalBody,
  Spinner,
  Badge,
} from "reactstrap";
import Select from "react-select";
import { useDataFetch } from "../../Hooks/Use-data-fetch";
import DataTable from "../../Components/DataTable";
import { API_BASE_URL } from "../../api/api";
import { getUserRanksColumns } from "../../Components/loyalty/UserRanksColumns";
import { UserRankModal } from "../../Components/loyalty/UserRankModal";
import { PageHeader } from "../../Components/common/PageHeader";

export const UserRanksList = () => {
  const { getData } = useDataFetch();

  const [filters, setFilters] = useState({ rankId: "" });
  const [appliedFilters, setAppliedFilters] = useState({ rankId: "" });
  const [pagination, setPagination] = useState({ pageIndex: 0, pageSize: 10 });
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedRank, setSelectedRank] = useState(null);
  const [previewModalOpen, setPreviewModalOpen] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  const [loadingPreview, setLoadingPreview] = useState(false);

  const { data: ranksSummary } = getData(
    ["user-ranks-summary"],
    "/settlements/user-ranks/summary"
  );

  const rankOptions = useMemo(
    () =>
      ranksSummary?.data?.map((r) => {
        const start = r.period.start.split('T')[0].split('-').reverse().join('/');
        const end = r.period.end.split('T')[0].split('-').reverse().join('/');
        return {
          value: r.id,
          label: `${start} - ${end}`,
        };
      }) || [],
    [ranksSummary]
  );

  const { data, isLoading } = getData(
    ["user-ranks", appliedFilters, pagination],
    "/settlements/user-ranks",
    {},
    {
      page: pagination.pageIndex + 1,
      limit: pagination.pageSize,
      ...(appliedFilters.rankId ? { id: appliedFilters.rankId } : {}),
    }
  );

  const columns = useMemo(
    () => getUserRanksColumns(setSelectedRank, setModalOpen),
    []
  );

  const handleSearch = () => {
    setPagination((prev) => ({ ...prev, pageIndex: 0 }));
    setAppliedFilters({
      rankId: filters.rankId || "",
    });
  };

  const handleGenerateExcel = () => {
    const params = new URLSearchParams();
    params.set("page", pagination.pageIndex + 1);
    params.set("limit", pagination.pageSize);
    if (appliedFilters.rankId) params.set("id", appliedFilters.rankId);

    window.open(
      `${API_BASE_URL}/settlements/report/excel/user-ranks?${params.toString()}`,
      "_blank"
    );
  };

  const handlePreviewCurrentMonth = async () => {
    setLoadingPreview(true);
    setPreviewModalOpen(true);

    try {
      const response = await fetch(
        `${API_BASE_URL}/settlements/preview/monthly-user-ranks`,
        {
          headers: {
            "Content-Type": "application/json",
          },
        }
      );

      const result = await response.json();

      if (result.success) {
        setPreviewData(result.data);
      } else {
        console.error("Error en preview:", result);
        setPreviewData(null);
      }
    } catch (error) {
      console.error("Error obteniendo preview:", error);
      setPreviewData(null);
    } finally {
      setLoadingPreview(false);
    }
  };

  return (
    <Fragment>
      <PageHeader
        title="Rangos de usuarios"
        crumbs={[{ label: "Liquidaciones" }, { label: "Rangos de usuarios" }]}
        actions={
          <>
            <Btn attrBtn={{ color: "warning", size: "sm", onClick: handlePreviewCurrentMonth }}>
              <i className="ri-eye-line me-1" /> Visualizar mes actual
            </Btn>
            <button type="button" className="btn btn-outline-primary btn-sm d-flex align-items-center gap-1" onClick={handleGenerateExcel}>
              <i className="ri-file-excel-2-line" /> Excel
            </button>
          </>
        }
      />

      <DataTable
        data={data?.data ?? []}
        total={data?.pagination?.total ?? 0}
        pagination={pagination}
        setPagination={setPagination}
        columns={columns}
        progressPending={isLoading}
        emptyMessage="No hay liquidaciones de rangos para mostrar."
        filters={
          <div className="d-flex align-items-end gap-2 flex-wrap w-100">
            <FormGroup className="mb-0" style={{ minWidth: 240 }}>
              <Label className="form-label mb-1">Periodo</Label>
              <Select
                classNamePrefix="react-select"
                isClearable
                placeholder="Selecciona"
                options={rankOptions}
                value={
                  rankOptions.find((opt) => opt.value === filters.rankId) ||
                  null
                }
                onChange={(option) =>
                  setFilters((prev) => ({
                    ...prev,
                    rankId: option ? option.value : "",
                  }))
                }
              />
            </FormGroup>
            <button type="button" className="btn btn-primary btn-sm" onClick={handleSearch}>
              <i className="ri-search-line me-1" /> Buscar
            </button>
          </div>
        }
      />

      <UserRankModal
        isOpen={modalOpen}
        toggle={() => setModalOpen(false)}
        rank={selectedRank}
      />

      {/* Modal de Preview — sin diseño todavía, se conserva tal como está */}
      <Modal
        isOpen={previewModalOpen}
        toggle={() => setPreviewModalOpen(false)}
        size="xl"
      >
        <ModalHeader toggle={() => setPreviewModalOpen(false)}>
          Visualizar - Rangos del Mes Actual
        </ModalHeader>
        <ModalBody>
          {loadingPreview ? (
            <div className="text-center py-5">
              <Spinner color="primary" />
              <p className="mt-3">Calculando rangos...</p>
            </div>
          ) : previewData ? (
            <div>
              <div className="alert alert-info mb-3">
                <i className="fa fa-info-circle me-2"></i>
                <strong>Período:</strong>{" "}
                {new Date(previewData.period.start).toLocaleDateString()} -{" "}
                {new Date(previewData.period.end).toLocaleDateString()}
                <br />
                <strong>Usuarios procesados:</strong> {previewData.usersProcessed}
              </div>

              <div className="table-responsive">
                <table className="table table-bordered table-hover">
                  <thead className="table-dark">
                    <tr>
                      <th>Usuario</th>
                      <th>Total Puntos</th>
                      <th>Rango</th>
                      <th>Directos Activos</th>
                      <th>Niveles</th>
                    </tr>
                  </thead>
                  <tbody>
                    {previewData.userRanks?.map((userRank, idx) => (
                      <tr key={idx}>
                        <td>{userRank.user_id}</td>
                        <td>
                          <strong>{userRank.total_points.toLocaleString()}</strong>
                        </td>
                        <td>
                          {userRank.rank ? (
                            <Badge color={userRank.rank.qualifies ? "success" : "warning"}>
                              {userRank.rank.qualifies ? "✅" : "⚠️"}{" "}
                              {userRank.rank.name || "Sin Rango"}
                            </Badge>
                          ) : (
                            <Badge color="secondary">Sin Rango</Badge>
                          )}
                        </td>
                        <td>
                          {userRank.rank ? (
                            <div>
                              <span className={userRank.rank.qualifies ? "text-success fw-bold" : "text-warning fw-bold"}>
                                {userRank.rank.active_level1_users}
                              </span>
                              <span className="text-muted">
                                {" "}/ {userRank.rank.required_active_users} requeridos
                              </span>
                              {!userRank.rank.qualifies && (
                                <small className="text-danger d-block">
                                  Faltan {userRank.rank.required_active_users - userRank.rank.active_level1_users} directos
                                </small>
                              )}
                            </div>
                          ) : "—"}
                        </td>
                        <td>
                          {userRank.levels?.map((level, levelIdx) => (
                            <div key={levelIdx} className="mb-1">
                              <Badge color="primary" className="me-2">
                                Nivel {level.level}
                              </Badge>
                              {level.points.toLocaleString()} pts
                              <span className="text-muted ms-2">
                                ({level.usersIds?.length || 0} usuarios,{" "}
                                {level.orders?.length || 0} órdenes)
                              </span>
                            </div>
                          ))}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ) : (
            <div className="alert alert-warning">
              No se pudo obtener el preview. Intenta nuevamente.
            </div>
          )}
        </ModalBody>
      </Modal>
    </Fragment>
  );
};
